import { Instagram, Play, ExternalLink } from 'lucide-react';

const INSTAGRAM_HANDLE = 'modularfurniturestore';
const INSTAGRAM_URL = `https://www.instagram.com/${INSTAGRAM_HANDLE}/`;

// Static reel data — thumbnails are self-hosted product images, links go directly to Instagram.
// No third-party embed scripts (Instagram embed.js) are used — this prevents Google Safe Browsing flags.
const REELS = [
  {
    id: 1,
    thumbnail: 'https://images.unsplash.com/photo-1549637642-90187f64f420?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtb2R1bGFyJTIwb2ZmaWNlJTIwd29ya3N0YXRpb24lMjBzZXR1cHxlbnwwfHx8fDE3ODIyMjQ3NjV8MA&ixlib=rb-4.1.0&q=85',
    caption: 'Modular workstations — assembled in minutes, built to last decades.',
    alt: 'Modern modular office workstation setup with ergonomic chairs',
    url: 'https://www.instagram.com/reel/DViey1ZFSkK/?igsh=ZnN1Nm84c3Fmczg1',
    isReel: true,
  },
  {
    id: 2,
    thumbnail: 'https://images.unsplash.com/photo-1718220216044-006f43e3a9b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtb2R1bGFyJTIwb2ZmaWNlJTIwd29ya3N0YXRpb24lMjBzZXR1cHxlbnwwfHx8fDE3ODIyMjQ3NjV8MA&ixlib=rb-4.1.0&q=85',
    caption: '4-seater cluster workstation — perfect for collaborative teams.',
    alt: 'Open plan office with modular desks and chairs',
    url: 'https://www.instagram.com/reel/DVRekE0icR0/?igsh=MXZnNnVrZWswenl6OQ==',
    isReel: true,
  },
  {
    id: 3,
    thumbnail: 'https://images.unsplash.com/photo-1706689656095-168768dc20a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBtb2R1bGFyJTIwb2ZmaWNlJTIwd29ya3N0YXRpb24lMjBzZXR1cHxlbnwwfHx8fDE3ODIyMjQ3NjV8MA&ixlib=rb-4.1.0&q=85',
    caption: 'Complete office delivered & installed — before and after transformation.',
    alt: 'Modern office with desk chairs and potted plant',
    url: 'https://www.instagram.com/p/DVqGs1fkcT0/?igsh=MWV0MWlxc2l4dWU3OA==',
    isReel: false,
  },
  {
    id: 4,
    thumbnail: 'https://images.pexels.com/photos/8297855/pexels-photo-8297855.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    caption: 'Factory-direct modular furniture — no middlemen, no markups.',
    alt: 'Spacious modern office with colorful dividers and workstations',
    url: 'https://www.instagram.com/p/DVifJBuEQB6/?igsh=MTg1aDg0b3NhMGlvcw==',
    isReel: false,
  },
];

export function InstagramReelsSection() {
  return (
    <section className="py-14 bg-white" data-testid="instagram-reels-section">
      <div className="container mx-auto px-4 sm:px-8 max-w-6xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-lg flex items-center justify-center">
              <Instagram className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-extrabold text-[#1a1a1a] uppercase tracking-tight">
                Follow Us on Instagram
              </h2>
              <p className="text-xs text-gray-500">@{INSTAGRAM_HANDLE}</p>
            </div>
          </div>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#1a1a1a] text-[#1a1a1a] text-xs font-bold uppercase tracking-wider hover:bg-[#1a1a1a] hover:text-white transition-colors"
            data-testid="instagram-follow-btn"
          >
            <Instagram className="w-3.5 h-3.5" />
            Follow @{INSTAGRAM_HANDLE}
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Reels Grid — 4 static thumbnails with hover overlay */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {REELS.map((reel) => (
            <a
              key={reel.id}
              href={reel.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative aspect-[9/12] overflow-hidden bg-[#f5f3ee] block"
              data-testid={`instagram-reel-${reel.id}`}
            >
              {/* Thumbnail */}
              <img
                src={reel.thumbnail}
                alt={reel.alt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
                width="300"
                height="400"
              />

              {/* Play icon overlay */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/30">
                <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                  <Play className="w-5 h-5 text-[#1a1a1a] ml-0.5" fill="#1a1a1a" />
                </div>
              </div>

              {/* Reel/Post badge */}
              <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 flex items-center gap-1">
                <Play className="w-2.5 h-2.5" fill="white" />
                {reel.isReel ? 'Reel' : 'Post'}
              </div>

              {/* Caption on hover */}
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-white text-xs leading-relaxed line-clamp-2">{reel.caption}</p>
              </div>
            </a>
          ))}
        </div>

        {/* Subtle follow prompt */}
        <p className="text-center text-xs text-gray-400 mt-4">
          See more workspace transformations on our Instagram
        </p>
      </div>
    </section>
  );
}
